import type { MouseEvent} from 'react'

import { useCallback } from 'react'
import { useState } from 'react'
import useSearchParamValues from "~/hooks/useSearchParamValues"

import { difference } from '~/utils/date'

import { SliderSizes, SliderVariants } from '@gotamedia/fluffy/components/Slider'
import useViewSize from '~/hooks/useViewSize'
import type { JobPayload } from '~/modules/jobs/types'
import * as Styled from './style'
import type * as Types from './types'

import { useLoaderData } from '@remix-run/react'
import type { SearchResponse } from '~/modules/jobs/types'

import Icon, {
    Icons,
    IconSizes,
    IconVariants
} from '@gotamedia/fluffy/Icon'
import slugify from 'slugify'
import { ItemType } from '~/services/resolvers/types'
import NoResults from '../NoResults/NoResults'

const JobWidgetCards: Types.JobWidgetCardsComponent = (props: Types.JobWidgetCardProps) => {
    const { legacy } = useSearchParamValues(['legacy'])
    const legacyMode = legacy === "true"

    const [index, setIndex] = useState(0)
    const [clientX, setClientX] = useState(0)
    const [hover, setHover] = useState(false)

    const data = useLoaderData<SearchResponse>()

    const { isLarge, isMedium, isSmall } = useViewSize()

    const today = new Date()

    const filteredItems = data.payload.filter(item => today <= new Date(item.promoUnpublished.replace(/-/g, "/")));

    const {
        width,
    } = props

    const chunk = (arr:JobPayload[] , size: number) => {
        return Array.from({ length: Math.ceil(arr.length / size) }, (v, i) =>
            arr.slice(i * size, i * size + size)
        )
    }

    const updateClientX = useCallback((clientX: number) => {
        setClientX(clientX)
    }, [])

    const updateIndex = useCallback((newIndex: number) => {
        setIndex(newIndex)
    }, [])

    const dragOrClick = useCallback((event: MouseEvent, companyName: string, uuid: string ) => {
        if (event.clientX < clientX + 5 && event.clientX > clientX - 5) {
            window.open(`${companyName ? `/${companyName}` : "" }/${uuid}`)
        }
    }, [clientX])

    const chunksize = width && width > 0 ? Math.floor(width / 190) : isLarge() ? 4 : !isSmall() ? 3 : 2

    // Only used for NoResults
    const height = legacyMode ? 200 : isSmall() ? 216 : 196;

    const result = filteredItems ? chunk(filteredItems, chunksize) : []

    return (
        <Styled.SliderContainer>
            {result.length > 0 ? (
                <Styled.Slider
                    legacy={legacyMode}
                    width={width || 0}
                    index={index}
                    variant={SliderVariants.Contrast}
                    size={SliderSizes.Small}
                    onChange={(newIndex: number) => updateIndex(newIndex)}
                    onMouseDown={(e: MouseEvent) => updateClientX(e.clientX)}
                    loop={false}
                    slidesCount={result.length}
                    shouldAutoPlay={!hover}
                    onMouseEnter={() => setHover(true)}
                    onMouseLeave={() => setHover(false)}
                    dynamicBullets={!isLarge() && (data.payload?.length / 3) > 3}
                >
                    <Styled.Slider.Slides slideProps={{
                        style: {
                            width: 'auto'
                        }
                    }}>
                        {result?.map((item, idx) => {
                            return (
                                <Styled.CardContainer key={`${idx}`}>
                                    {
                                        item.map((it: JobPayload, idx) => {
                                            const companyName = slugify(it?.companyName || 'jobb', {
                                                locale: 'sv'
                                            })
                                            return (
                                                <Styled.CardWrapper
                                                    legacy={legacyMode}
                                                    onMouseUp={(e) => dragOrClick(e, companyName, it.uuid)}
                                                    key={`${it.uuid}-${idx}`}
                                                    width={width ? (((width-chunksize-44) / chunksize)-10) : undefined}
                                                >
                                                    <Styled.ImageContainer legacy={legacyMode}>
                                                        <Styled.CardImage src={it.logoUrl} />
                                                    </Styled.ImageContainer>
                                                    <Styled.TextContainer>
                                                        <Styled.CardName>
                                                            {it.title}
                                                        </Styled.CardName>
                                                        <Styled.CardInfo>
                                                            <Icon
                                                                icon={Icons.MapPin}
                                                                size={IconSizes.Tiny}
                                                                variant={IconVariants.Solid}
                                                            />
                                                            <Styled.InfoText>
                                                                {it?.placements.join(', ')}
                                                            </Styled.InfoText>
                                                        </Styled.CardInfo>
                                                        <Styled.CardInfo>
                                                            {difference(it.applicationDeadline) > 0 ? (
                                                                <>
                                                                    <Icon
                                                                        icon={Icons.Clock}
                                                                        size={IconSizes.Tiny}
                                                                        variant={IconVariants.Solid}
                                                                    />
                                                                    <Styled.InfoText>
                                                                        {difference(it.applicationDeadline)} dagar kvar
                                                                    </Styled.InfoText>
                                                                </>
                                                            ) : null}
                                                        </Styled.CardInfo>
                                                    </Styled.TextContainer>
                                                </Styled.CardWrapper>
                                            )
                                        })

                                    }
                                </Styled.CardContainer>
                            )
                        })}
                    </Styled.Slider.Slides>
                    {
                        data.payload?.length > chunksize ? (
                            <>
                                <Styled.Slider.SliderBullets />
                                {
                                    (isLarge() || isMedium() && !legacyMode) && hover ? (
                                        <Styled.Slider.Navigation />
                                    ) : (
                                        null
                                    )
                                }
                            </>
                        ) : (
                            null
                        )
                    }
                </Styled.Slider>
            ) : (
                <NoResults heigth={height} type={ItemType.Job} width={width}/>
            )}
        </Styled.SliderContainer>
    )
}

export default JobWidgetCards
